// src/components/ProfileCheckerPhone.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  TextField,
  Button,
  useMediaQuery,
  LinearProgress,
  Autocomplete,
  Popper,
  Avatar,
  Chip,
  InputAdornment,
  IconButton,
  CircularProgress,
  Alert,
  Snackbar,
  Divider,
} from '@mui/material';
import {
  Search as SearchIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Business as BusinessIcon,
  Clear as ClearIcon,
  CheckCircle as CheckCircleIcon,
  Email as EmailIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';

import Sidebar from '../../components/sidebars/Sidebar_auditor';
import TopNav from '../../components/top_bars/TopNav_inventory';

import { useNavigate } from 'react-router-dom';

// ==================== CONSTANTS ====================
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

// ==================== HELPER FUNCTIONS ====================

/**
 * Sanitize string input to prevent XSS attacks
 */
const sanitizeString = (str) => {
  if (!str || typeof str !== 'string') return '';
  return str
    .trim()
    .replace(/[<>]/g, '')
    .replace(/[&]/g, '&amp;')
    .replace(/["]/g, '&quot;')
    .replace(/[']/g, '&#x27;')
    .replace(/[/]/g, '&#x2F;');
};

/**
 * Log security events
 */
const logSecurityEvent = (eventType, details) => {
  const logEntry = {
    timestamp: new Date().toISOString(),
    eventType,
    details,
    userAgent: navigator.userAgent,
    url: window.location.href,
  };
  console.debug(`[SECURITY] ${eventType}:`, logEntry);
};

// ==================== MAIN COMPONENT ====================
const AuditorProfileCheckerPhone = () => {
  const isMobile = useMediaQuery('(max-width:768px)');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  
  // Search states
  const [searchInput, setSearchInput] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState('');
  
  // Loading and navigation states
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  const navigate = useNavigate();
  const searchDebounceRef = useRef(null);

  const toggleSidebar = () => {
    setSidebarOpen((prev) => !prev);
    console.debug('[ProfileChecker] Sidebar toggled:', !sidebarOpen);
  };

  // Load stored phone number on mount
  useEffect(() => {
    const storedPhone = localStorage.getItem('phoneNumber');
    if (storedPhone) {
      console.debug('[ProfileChecker] Found stored phone number:', storedPhone);
      setSearchInput(storedPhone);
    }
  }, []);

  /**
   * Search employees by name or phone using get_all_active_employees.php API
   */
  const searchEmployees = useCallback(async (searchTerm) => {
    if (!searchTerm || searchTerm.trim().length < 2) {
      setSearchResults([]);
      setSearchError('');
      return;
    }

    setSearchLoading(true);
    setSearchError('');
    
    try {
      console.debug(`[ProfileChecker] Searching employees with term: "${searchTerm}"`);
      
      const response = await fetch(`${API_BASE_URL}/get_all_active_employees.php`);
      const result = await response.json();
      
      if (result.success && result.data) {
        const allEmployees = result.data;
        const searchLower = searchTerm.toLowerCase();
        
        const filtered = allEmployees.filter(emp => 
          (emp.full_name || '').toLowerCase().includes(searchLower) ||
          (emp.surname || '').toLowerCase().includes(searchLower) ||
          (emp.other_names || '').toLowerCase().includes(searchLower) ||
          (emp.phone || '').includes(searchTerm) ||
          (emp.email || '').toLowerCase().includes(searchLower)
        );
        
        setSearchResults(filtered);
        console.debug(`[ProfileChecker] Found ${filtered.length} employees matching "${searchTerm}"`);
        
        if (filtered.length === 0) {
          setSearchError('No employees found matching your search');
        }
      } else {
        setSearchResults([]);
        setSearchError('Failed to load employee list');
        console.error('[ProfileChecker] API error:', result.message);
      }
    } catch (error) {
      console.error('[ProfileChecker] Error searching employees:', error);
      setSearchResults([]);
      setSearchError('Error searching for employees. Please try again.');
    } finally {
      setSearchLoading(false);
    }
  }, []);

  /**
   * Handle employee selection from dropdown
   */
  const handleEmployeeSelect = (event, employee) => {
    if (employee) {
      console.debug(`[ProfileChecker] Employee selected: ${employee.full_name} (${employee.phone})`);
      setSelectedEmployee(employee);
      setSearchInput(`${employee.full_name} (${employee.phone})`);
      setSearchResults([]);
      setSearchError('');
    } else {
      handleClearSelection();
    }
  };

  /**
   * Clear the current selection
   */
  const handleClearSelection = () => {
    console.debug('[ProfileChecker] Clearing employee selection');
    setSelectedEmployee(null);
    setSearchInput('');
    setSearchResults([]);
    setSearchError('');
  };

  /**
   * Handle form submission - validates and navigates to staff profile
   */
  const handleSubmit = async () => {
    let phoneToSave = '';
    let employeeName = '';
    
    // If employee selected from dropdown, use their phone
    if (selectedEmployee) {
      phoneToSave = selectedEmployee.phone;
      employeeName = selectedEmployee.full_name;
      console.debug(`[ProfileChecker] Using selected employee: ${employeeName} (${phoneToSave})`);
    } 
    // If search input contains a phone number in parentheses (from autocomplete display)
    else {
      const phoneMatch = searchInput.match(/\(([0-9]{10,15})\)/);
      if (phoneMatch) {
        phoneToSave = phoneMatch[1];
        console.debug(`[ProfileChecker] Extracted phone from display: ${phoneToSave}`);
      }
    }
    
    // Validate phone number
    if (!phoneToSave) {
      setSnackbar({ 
        open: true, 
        message: 'Please select a valid employee from the search results', 
        severity: 'error' 
      });
      return;
    }
    
    // Validate phone number format
    if (!phoneToSave.match(/^[0-9]{10,15}$/)) {
      setSnackbar({ open: true, message: 'Invalid phone number format', severity: 'error' });
      return;
    }
    
    setLoading(true);
    
    try {
      console.debug(`[ProfileChecker] Verifying employee with phone: ${phoneToSave}`);
      
      const response = await fetch(`${API_BASE_URL}/get_employee_info.php?phone=${encodeURIComponent(phoneToSave)}`);
      const result = await response.json();
      
      if (result.success && result.data && result.data.employee) {
        const employee = result.data.employee;
        
        console.debug(`[ProfileChecker] Employee found:`, {
          id: employee.id,
          name: employee.full_name,
          status: employee.status,
          is_active: employee.is_active,
          department: employee.department
        });
        
        // Check if employee is active - Handle both string and number values
        const isActive = employee.status === 'Active' && (employee.is_active == 1);
        
        if (!isActive) {
          let inactiveReason = '';
          if (employee.status !== 'Active') {
            inactiveReason = `status is "${employee.status}"`;
          } else if (employee.is_active != 1) {
            inactiveReason = `is_active = ${employee.is_active}`;
          }
          
          console.warn(`[ProfileChecker] Employee not active: ${inactiveReason}`);
          setSnackbar({ 
            open: true, 
            message: `Employee ${employee.full_name} is not active (${inactiveReason}). Cannot access profile.`, 
            severity: 'error' 
          });
          setLoading(false);
          return;
        }
        
        logSecurityEvent('PROFILE_ACCESS_ATTEMPT', { 
          phone: phoneToSave, 
          employee_name: employee.full_name,
          department: employee.department
        });
        
        localStorage.setItem('phoneNumber', phoneToSave);
        
        setTimeout(() => {
          setLoading(false);
          navigate('/AuditorProfileChecker');
          logSecurityEvent('PROFILE_ACCESS_SUCCESS', { phone: phoneToSave });
        }, 1500);
        
      } else {
        console.error('[ProfileChecker] Employee not found:', result.message);
        setSnackbar({ 
          open: true, 
          message: result.message || `Employee with phone number ${phoneToSave} not found.`, 
          severity: 'error' 
        });
        setLoading(false);
      }
      
    } catch (error) {
      console.error('[ProfileChecker] Error verifying employee:', error);
      setSnackbar({ open: true, message: 'Error verifying employee. Please try again.', severity: 'error' });
      setLoading(false);
    }
  };

  // Debounced search
  useEffect(() => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    
    if (searchInput.trim().length >= 2 && !selectedEmployee) {
      searchDebounceRef.current = setTimeout(() => {
        searchEmployees(searchInput);
      }, 400);
    } else if (searchInput.trim().length < 2) {
      setSearchResults([]);
      setSearchError('');
    }
    
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, [searchInput, selectedEmployee, searchEmployees]);

  // Custom popper for autocomplete
  const CustomPopper = (props) => (
    <Popper {...props} placement="bottom-start" style={{ width: props.style?.width, zIndex: 1300 }}>
      <Paper elevation={4} sx={{ mt: 1, maxHeight: 400, overflow: 'auto' }}>
        {searchLoading && (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <CircularProgress size={30} />
            <Typography variant="body2" sx={{ mt: 1 }}>Searching employees...</Typography>
          </Box>
        )}
        {!searchLoading && searchError && !searchResults.length && (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="error">{searchError}</Typography>
          </Box>
        )}
        {!searchLoading && !searchError && searchResults.length === 0 && searchInput.length >= 2 && (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="textSecondary">
              No employees found
            </Typography>
          </Box>
        )}
        {props.children}
      </Paper>
    </Popper>
  );

  return (
    <Box sx={{ display: 'flex', height: '100vh', bgcolor: '#f5f5f5' }}>
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />

      <Box
        sx={{
          flexGrow: 1,
          p: 3,
          ml: isMobile ? 0 : sidebarOpen ? 30 : 5,
          maxWidth: '1400px',
          mx: 'auto',
          width: '100%',
          overflow: 'auto',
          height: '100vh',
        }}
      >
        <TopNav
          onLogout={() => {
            logSecurityEvent('USER_LOGOUT', {});
            localStorage.removeItem('authToken');
            localStorage.removeItem('userProfile');
            localStorage.removeItem('phoneNumber');
          }}
          onNotificationClick={() => console.debug('[ProfileChecker] Notification clicked')}
          toggleSidebar={toggleSidebar}
        />

       

        <Grid container spacing={3} sx={{ mt: 4, justifyContent: 'center' }}>
          <Grid item xs={12} md={8} lg={6}>
            <Paper
              elevation={4}
              sx={{
                p: 4,
                borderRadius: 4,
                boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.12)',
                background: '#ffffff',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Avatar sx={{ bgcolor: '#C84646', width: 56, height: 56 }}>
                  <PersonIcon sx={{ fontSize: 32 }} />
                </Avatar>
                <Box>
                  <Typography variant="h5" fontWeight="bold" color="#C84646" sx={{ fontFamily: 'Times New Roman, serif' }}>
                    Staff Profile Access
                  </Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ fontFamily: 'Times New Roman, serif' }}>
                    Search by employee name or phone to view profile
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ mb: 3 }} />

           
              {/* Employee Search with Autocomplete */}
              <Autocomplete
                freeSolo
                options={searchResults}
                getOptionLabel={(option) => {
                  if (typeof option === 'string') return option;
                  return `${option.surname || ''} ${option.other_names || ''} (${option.phone || ''})`;
                }}
                value={selectedEmployee}
                onChange={handleEmployeeSelect}
                inputValue={searchInput}
                onInputChange={(event, newInputValue, reason) => {
                  if (reason === 'input') {
                    setSearchInput(newInputValue);
                    if (selectedEmployee) {
                      setSelectedEmployee(null);
                    }
                  }
                }}
                loading={searchLoading}
                PopperComponent={CustomPopper}
                filterOptions={(x) => x}
                disabled={loading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Type employee name (e.g., John, Nicholas)..."
                    variant="outlined"
                    error={!!searchError && !selectedEmployee}
                    helperText={searchError && !selectedEmployee ? searchError : "Enter at least 2 characters to search"}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start">
                            <SearchIcon sx={{ color: '#C84646' }} />
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                      endAdornment: (
                        <>
                          {searchLoading && <CircularProgress size={20} />}
                          {(selectedEmployee || searchInput) && !loading && (
                            <IconButton size="small" onClick={handleClearSelection} sx={{ mr: 1 }}>
                              <ClearIcon />
                            </IconButton>
                          )}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                    sx={{ mb: 2 }}
                  />
                )}
                renderOption={(props, option) => {
                  return (
                    <li {...props}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', py: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ bgcolor: '#C84646', width: 40, height: 40 }}>
                            <PersonIcon />
                          </Avatar>
                          <Box>
                            <Typography variant="body1" fontWeight="bold" sx={{ fontFamily: 'Times New Roman, serif' }}>
                              {option.surname} {option.other_names}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                              <Chip 
                                size="small" 
                                icon={<BusinessIcon sx={{ fontSize: 14 }} />} 
                                label={option.department || 'No Department'} 
                                variant="outlined" 
                              />
                              <Chip 
                                size="small" 
                                icon={<PhoneIcon sx={{ fontSize: 14 }} />} 
                                label={option.phone} 
                                variant="outlined" 
                              />
                              {option.email && (
                                <Chip 
                                  size="small" 
                                  icon={<EmailIcon sx={{ fontSize: 14 }} />}
                                  label={option.email} 
                                  variant="outlined" 
                                />
                              )}
                            </Box>
                          </Box>
                        </Box>
                        <Chip 
                          size="small" 
                          label={option.status === 'Active' ? 'Active' : 'Inactive'} 
                          color={option.status === 'Active' ? 'success' : 'default'}
                        />
                      </Box>
                    </li>
                  );
                }}
              />

              {/* Selected Employee Preview */}
              {selectedEmployee && !loading && (
                <Alert 
                  severity={selectedEmployee.status === 'Active' ? 'success' : 'warning'} 
                  icon={selectedEmployee.status === 'Active' ? <CheckCircleIcon /> : <WarningIcon />}
                  sx={{ mt: 2, borderRadius: 2 }}
                >
                  <Typography variant="body2" sx={{ fontFamily: 'Times New Roman, serif' }}>
                    <strong>Selected Employee:</strong> {selectedEmployee.surname} {selectedEmployee.other_names}<br />
                    <strong>Department:</strong> {selectedEmployee.department || 'N/A'}<br />
                    <strong>Phone:</strong> {selectedEmployee.phone}<br />
                    <strong>Email:</strong> {selectedEmployee.email || 'N/A'}<br />
                    <strong>Status:</strong> {selectedEmployee.status || 'Active'}
                  </Typography>
                </Alert>
              )}

              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={loading || !selectedEmployee}
                sx={{
                  mt: 4,
                  textTransform: 'none',
                  fontWeight: 'bold',
                  borderRadius: 2,
                  backgroundColor: '#C84646',
                  py: 1.5,
                  width: '100%',
                  '&:hover': {
                    backgroundColor: '#b33c3c',
                  },
                  fontFamily: 'Times New Roman, serif',
                }}
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <PersonIcon />}
              >
                {loading ? 'Verifying & Loading Profile...' : 'View Staff Profile'}
              </Button>

              {loading && (
                <Box sx={{ mt: 3 }}>
                  <LinearProgress sx={{ borderRadius: 2 }} />
                  <Typography variant="body2" sx={{ mt: 1, textAlign: 'center', fontFamily: 'Times New Roman, serif' }}>
                    Loading profile, please wait...
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>

        {/* Footer */}
        <footer style={{ textAlign: 'center', padding: '20px', marginTop: 'auto' }}>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="body2" color="textSecondary" sx={{ fontFamily: 'Times New Roman, serif' }}>
            © {new Date().getFullYear()} Elegantè IT - Staff Profile Access System
          </Typography>
          <Typography variant="caption" color="textSecondary" sx={{ fontFamily: 'Times New Roman, serif' }}>
            Search by employee name to access staff profile
          </Typography>
        </footer>
      </Box>

      {/* Snackbar for notifications */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={() => setSnackbar({ ...snackbar, open: false })} 
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          severity={snackbar.severity} 
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          variant="filled"
          sx={{ fontFamily: 'Times New Roman, serif' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AuditorProfileCheckerPhone;